import { useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
// @mui
import { alpha } from "@mui/material/styles";
import {
  Box,
  Divider,
  Typography,
  Stack,
  MenuItem,
  Avatar,
  IconButton,
} from "@mui/material";
// components
import MenuPopover from "../../components/MenuPopover";
import { useDispatch, useSelector } from "react-redux";
import { logoutAsync } from "../../slices/authSlice";
import { resolvePrimaryImageSrc } from "src/utils/imageLinks";
import { useUserProfile } from "src/hooks/account";
import { SupportsWebp } from "src/slices/appSlice";

// ----------------------------------------------------------------------

const MENU_OPTIONS = [
  {
    label: "Cart",
    icon: "mdi:cart",
    linkTo: "/account/cart",
  },
  {
    label: "Profile",
    icon: "person-outline",
    linkTo: "/account/profile",
  },
  {
    label: "My Tributes",
    icon: "eva:archive-outline",
    linkTo: "/account/uploads",
  },
  {
    label: "Orders",
    icon: "eva:archive-outline",
    linkTo: "/account/order",
  },
];

// ----------------------------------------------------------------------

export default function AccountPopover({ user }) {
  const dispatch = useDispatch();
  const profileQuery = useUserProfile();
  const supportsWebp = useSelector(SupportsWebp);

  // need to tweak this to better handle not logged on user
  const actualUser = user || {
    firstName: "Not",
    lastName: "Logged In",
    email: "not logged on",
  };

  const initials = `${actualUser.firstName[0]}${actualUser.lastName[0]}`;

  const anchorRef = useRef(null);

  const [open, setOpen] = useState(null);

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleLogout = () => {
    dispatch(logoutAsync());
  };

  return (
    <>
      <IconButton
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          p: 0,
          ...(open && {
            "&:before": {
              zIndex: 1,
              content: "''",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              position: "absolute",
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.5),
            },
          }),
        }}
      >
        <Avatar
          sx={{
            bgcolor: (theme) => theme.palette.primary.dark,
            border: "2px solid",
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
          }}
        >
          {!profileQuery.data?.primaryImageFileNamePrefix ? (
            initials
          ) : (
            <img
              src={resolvePrimaryImageSrc(
                "user",
                profileQuery.data?.id,
                profileQuery.data?.primaryImageFileNamePrefix,
                "sm",
                supportsWebp ? "webp" : "jpg"
              )}
              alt={"Profile"}
            />
          )}
        </Avatar>
      </IconButton>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          "& .MuiMenuItem-root": {
            typography: "body2",
            borderRadius: 0.75,
          },
        }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle2" noWrap>
            {actualUser.firstName} {actualUser.lastName}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
            {actualUser.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: "dashed" }} />

        <Stack sx={{ p: 1 }}>
          {MENU_OPTIONS.map((option) => (
            <MenuItem
              key={option.label}
              to={option.linkTo}
              component={RouterLink}
              onClick={handleClose}
            >
              {option.label}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: "dashed" }} />

        <MenuItem onClick={handleLogout} sx={{ m: 1 }}>
          Logout
        </MenuItem>
      </MenuPopover>
    </>
  );
}
